import { Chip as MuiChip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import styled from 'styled-components';

import { generateChipCss } from './Chip.styles';
import type { ChipProps } from './Chip.types';

const StyledChip = styled(MuiChip).withConfig({
  shouldForwardProp: (prop) =>
    !['variant', 'rounded', 'chipColor', 'customSize', 'theme'].includes(
      prop as string
    ),
})<ChipProps & { customSize?: 'small' | 'large' }>`
  ${({ theme, customSize, ...props }) =>
    generateChipCss(theme, { size: customSize, ...props })}
`;

/**
 * Custom Chip component that extends MUI Chip with additional styling and variants
 *
 * @example
 * ```tsx
 * <Chip label="Brand" chipColor="brand-blue" />
 * <Chip label="Large" size="large" />
 * <Chip label="Custom" chipColor="green" onDelete={() => {}} />
 * ```
 */
const Chip: React.FC<ChipProps> = ({
  size = 'small',
  chipColor = 'white',
  variant = 'outlined',
  ...props
}) => {
  const theme = useTheme();

  return (
    <StyledChip
      {...props}
      customSize={size}
      theme={theme}
      chipColor={chipColor}
      variant={variant}
    />
  );
};

Chip.displayName = 'Chip';

export default Chip;
